import type { ComponentType, ReactNode } from "react";
import { useState } from "react";
import { Link, useLocation } from "wouter";
import {
  AppBar,
  Box,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useAuth } from "./auth";
import { membershipRoleLabel, type Membership } from "./types";
import { panelForRole } from "./panels";
import { CCS_PALETTE } from "./theme";

const DRAWER_WIDTH = 264;

export interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

/**
 * Sidebar shell shared by every admin panel.
 *
 * Visual language follows the Minimal UI Kit free kit:
 * - purpleTint sidebar surface
 * - katanaPurple active state with tinted background pill
 * - Minimal-style nav item typography and spacing
 * - Section group headers in Minimal's overline style
 */
export function AppShell({
  title,
  nav,
  children,
}: {
  title: string;
  nav: NavItem[] | NavGroup[];
  children: ReactNode;
}) {
  const { user, membership, allMemberships, logout, switchMembershipAndNavigate } = useAuth();
  const [location] = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [switching, setSwitching] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);

  // Every OTHER active membership, including ones served by a sibling panel —
  // switching navigates to the panel that owns the role.
  const otherMemberships = allMemberships.filter(
    (m) =>
      m.status === "active" &&
      m.membershipId !== membership?.membershipId &&
      m.role !== "pharmacist",
  );

  const handleSwitch = async (m: Membership) => {
    setMenuAnchor(null);
    setSwitchError(null);
    setSwitching(true);
    try {
      await switchMembershipAndNavigate(m);
    } catch (err) {
      console.error("[AppShell] switch failed", err);
      setSwitchError(
        err instanceof Error ? err.message : "Failed to switch role. Please try again.",
      );
      setSwitching(false);
    }
  };

  const drawerContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: CCS_PALETTE.purpleTint,
        borderRight: "1px solid",
        borderColor: "divider",
      }}
    >
      {/* Brand header — matches navbar height, vertically centered */}
      <Toolbar
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "96px !important",
          px: 1.5,
        }}
      >
        <Typography
          variant="h6"
          noWrap
          sx={{
            fontWeight: 700,
            color: CCS_PALETTE.katanaPurple,
            letterSpacing: "-0.01em",
            textAlign: "center",
          }}
        >
          {title}
        </Typography>
      </Toolbar>

      {/* Nav items — strictly below the title container */}
      <Box
        component="nav"
        sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", px: 1.5, pb: 1 }}
      >
        <List disablePadding>
          {(() => {
            const groups: NavGroup[] =
              nav.length > 0 && "items" in nav[0]
                ? (nav as NavGroup[])
                : [{ label: "", items: nav as NavItem[] }];
            return groups.map((group) => (
              <Box key={group.label || "default"} sx={{ mb: 0.5 }}>
                {group.label && (
                  <ListSubheader
                    disableSticky
                    sx={{
                      bgcolor: "transparent",
                      px: 1,
                      pt: 1.5,
                      pb: 0.5,
                      lineHeight: "24px",
                    }}
                  >
                    {group.label}
                  </ListSubheader>
                )}
                {group.items.map(({ to, label, icon: Icon }) => {
                  const active = to === "/" ? location === "/" : location.startsWith(to);
                  return (
                    <ListItemButton
                      key={to}
                      component={Link}
                      href={to}
                      selected={active}
                      onClick={() => setMobileOpen(false)}
                      sx={{
                        mb: 0.5,
                        px: 1.5,
                        minHeight: 44,
                        borderRadius: 1.5,
                        ...(active && {
                          bgcolor: `${CCS_PALETTE.katanaPurple}14`,
                          color: CCS_PALETTE.katanaPurple,
                          "&:hover": { bgcolor: `${CCS_PALETTE.katanaPurple}20` },
                        }),
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: 1.5,
                          justifyContent: "center",
                          color: active ? CCS_PALETTE.katanaPurple : "text.secondary",
                        }}
                      >
                        <Icon className="" />
                      </ListItemIcon>
                      <ListItemText
                        primary={label}
                        slotProps={{
                          primary: {
                            variant: "body2",
                            noWrap: true,
                            sx: { fontWeight: active ? 600 : 400 },
                          },
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </Box>
            ));
          })()}
        </List>
      </Box>

      {/* Footer */}
      <Divider sx={{ borderStyle: "dashed" }} />
      <Box sx={{ p: 1.5 }}>
        {user?.email && (
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            component="div"
            sx={{ mb: 0.75 }}
          >
            {user.email}
          </Typography>
        )}

        {membership && (
          <Stack
            direction="row"
            spacing={0.5}
            sx={{ mb: 0.75, alignItems: "center" }}
          >
            <Chip
              label={membershipRoleLabel(
                membership.role,
                membership.chainId,
                membership.pharmacyId,
              )}
              size="small"
              sx={{
                fontSize: "0.65rem",
                maxWidth: 160,
                bgcolor: `${CCS_PALETTE.katanaPurple}14`,
                color: CCS_PALETTE.katanaPurple,
                fontWeight: 600,
                border: "none",
              }}
            />
            {otherMemberships.length > 0 && (
              <Tooltip title="Switch role">
                <IconButton
                  size="small"
                  onClick={(e) => setMenuAnchor(e.currentTarget)}
                  disabled={switching}
                  sx={{ p: 0.25 }}
                >
                  {switching ? (
                    <CircularProgress size={14} />
                  ) : (
                    <SwapHorizIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        )}

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          transformOrigin={{ horizontal: "left", vertical: "bottom" }}
          anchorOrigin={{ horizontal: "left", vertical: "top" }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ px: 2, pt: 1, pb: 0.5, display: "block", fontWeight: 600 }}
          >
            Switch active role
          </Typography>
          {otherMemberships.map((m) => (
            <MenuItem key={m.membershipId} onClick={() => handleSwitch(m)} dense>
              <Box>
                <Typography variant="body2">
                  {membershipRoleLabel(m.role, m.chainId, m.pharmacyId)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {panelForRole(m.role).label}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Menu>

        {switchError && (
          <Typography variant="caption" color="error" sx={{ display: "block", mb: 0.75 }}>
            {switchError}
          </Typography>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* Mobile top AppBar */}
      <AppBar
        position="fixed"
        color="default"
        sx={{
          display: { md: "none" },
          bgcolor: CCS_PALETTE.purpleTint,
          zIndex: (theme) => theme.zIndex.drawer + 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          boxShadow: "none",
        }}
      >
        <Toolbar sx={{ minHeight: "64px !important" }}>
          <IconButton
            edge="start"
            aria-label="Toggle navigation"
            onClick={() => setMobileOpen((o) => !o)}
            sx={{ mr: 1 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, color: CCS_PALETTE.katanaPurple, flex: 1 }}
          >
            {title}
          </Typography>
          <Tooltip title="Sign out">
            <IconButton
              size="small"
              onClick={() => logout()}
              sx={{
                color: "text.secondary",
                "&:hover": { bgcolor: `${CCS_PALETTE.katanaPurple}14`, color: CCS_PALETTE.katanaPurple },
              }}
            >
              <LogoutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {/* Mobile temporary drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": { width: DRAWER_WIDTH, boxSizing: "border-box" },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop permanent drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
            overflowX: "hidden",
            borderRight: "none",
            boxShadow: (theme) => theme.shadows[2],
          },
          width: DRAWER_WIDTH,
          flexShrink: 0,
        }}
        open
      >
        {drawerContent}
      </Drawer>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          mt: { xs: "64px", md: 0 },
          bgcolor: "background.default",
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

export function PageHeader({
  title,
  description,
  backHref,
  actions,
}: {
  title: string;
  description?: string;
  /** If provided, shows an ← arrow button in the controls row below the title. */
  backHref?: string;
  /** Rendered right-aligned in the controls row below the title. */
  actions?: ReactNode;
}) {
  const hasControls = Boolean(backHref || actions);
  const { logout } = useAuth();

  return (
    <>
      {/* Title bar — generous padding, original height */}
      <Box
        sx={{
          px: { xs: 2.5, md: 4 },
          py: { xs: 2.5, md: 3 },
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: "-0.01em" }}>
            {title}
          </Typography>
          {description ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {description}
            </Typography>
          ) : null}
        </Box>
        <Tooltip title="Sign out">
          <IconButton
            size="small"
            onClick={() => logout()}
            sx={{
              display: { xs: "none", md: "inline-flex" },
              color: "text.secondary",
              "&:hover": { bgcolor: `${CCS_PALETTE.katanaPurple}14`, color: CCS_PALETTE.katanaPurple },
            }}
          >
            <LogoutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Controls row — below the navbar, same horizontal padding as page content */}
      {hasControls && (
        <Box
          sx={{
            px: { xs: 2, md: 4 },
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          {backHref ? (
            <IconButton
              component={Link}
              href={backHref}
              size="small"
              aria-label="Back"
              sx={{
                color: "text.secondary",
                "&:hover": { color: "text.primary", bgcolor: "action.hover" },
              }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          ) : (
            <Box sx={{ width: 32 }} />
          )}
          {actions ? (
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
              {actions}
            </Stack>
          ) : (
            <Box />
          )}
        </Box>
      )}
    </>
  );
}
